import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { TOOLS_PERMS } from "@/lib/perms";
import { Button } from "@/components/ui/button";
import { InstallPrompt } from "@/components/InstallPrompt";
import {
  LayoutDashboard, GitCompareArrows, Package, Wallet, Receipt, Tags,
  Sparkles, Plug, LogOut, Sun, Moon, BarChart3, Cable, KeyRound, Wrench, Users, Menu, X,
} from "lucide-react";

const NAV_GROUPS = [
  { heading: null, items: [
    { to: "/", label: "Overview", icon: LayoutDashboard, title: "Sales Overview", perm: "overview" },
    { to: "/channels", label: "Channels", icon: GitCompareArrows, title: "Channel Comparison", perm: "channels" },
    { to: "/prices", label: "Prices", icon: Tags, title: "Price Comparison", perm: "prices" },
  ]},
  { heading: "Amazon", items: [
    { to: "/tools", label: "Amazon Tools", icon: Wrench, title: "Amazon Tools", perm: TOOLS_PERMS },
    { to: "/products", label: "Products", icon: Package, title: "Products & Inventory", perm: "products" },
    { to: "/settlements", label: "Settlements", icon: Wallet, title: "Settlement Analysis", perm: "settlements" },
    { to: "/fees", label: "MIS Report", icon: Receipt, title: "MIS Report — Fees vs Revenue", perm: "mis" },
  ]},
  { heading: "AI & Automation", items: [
    { to: "/assistant", label: "AI Assistant", icon: Sparkles, title: "Claude AI Assistant", perm: "assistant" },
    { to: "/mcp", label: "MCP Server", icon: Plug, title: "MCP Server Guide", perm: "mcp" },
  ]},
  { heading: "Settings", items: [
    { to: "/integrations", label: "Integrations", icon: Cable, title: "Channel Integrations", perm: "integrations" },
    { to: "/keys", label: "API Keys", icon: KeyRound, title: "API Key Management", perm: "api_keys" },
    { to: "/users", label: "Users", icon: Users, title: "User Management", perm: "__admin__", adminOnly: true },
  ]},
];
const NAV = NAV_GROUPS.flatMap((g) => g.items);
const COMING_SOON = [
  { label: "Flipkart", note: "awaiting approval" },
  { label: "Website", note: "awaiting API keys" },
];

export default function Layout() {
  const { user, logout, can } = useAuth();
  const location = useLocation();
  const [dark, setDark] = useState(() => localStorage.getItem("theme") === "dark");
  const [navOpen, setNavOpen] = useState(false);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("theme", dark ? "dark" : "light");
  }, [dark]);

  useEffect(() => { setNavOpen(false); }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = navOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [navOpen]);

  const allowed = (item) => item.adminOnly ? user?.role === "admin" : can(item.perm);
  const visibleGroups = NAV_GROUPS
    .map((g) => ({ ...g, items: g.items.filter(allowed) }))
    .filter((g) => g.items.length > 0);
  const current = NAV.find((n) => n.to === location.pathname) || NAV[0];

  return (
    <div className="flex min-h-screen bg-background text-foreground">
      {navOpen && (
        <button
          aria-label="Close menu"
          onClick={() => setNavOpen(false)}
          data-testid="nav-backdrop"
          className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm lg:hidden"
        />
      )}
      <aside
        className={`fixed inset-y-0 left-0 w-64 sm:w-60 bg-[#09090B] text-zinc-300 flex flex-col z-50
                    transition-transform duration-300 ease-out lg:translate-x-0
                    ${navOpen ? "translate-x-0 shadow-2xl" : "-translate-x-full"}`}
        data-testid="sidebar"
      >
        <div className="flex items-center gap-2.5 px-5 h-16 border-b border-zinc-800">
          <div className="h-8 w-8 rounded-md bg-blue-600 flex items-center justify-center">
            <BarChart3 className="h-4 w-4 text-white" aria-hidden="true" />
          </div>
          <span className="font-heading font-bold text-white tracking-tight">SND Commerce</span>
          <button onClick={() => setNavOpen(false)} data-testid="nav-close"
                  aria-label="Close menu"
                  className="ml-auto lg:hidden text-zinc-500 hover:text-white transition-colors">
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>
        <nav className="flex-1 px-3 py-5 space-y-4 overflow-y-auto">
          {visibleGroups.map((g, gi) => (
            <div key={gi} className="space-y-1">
              {g.heading && (
                <p className="px-3 pt-1 pb-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-600">{g.heading}</p>
              )}
              {g.items.map(({ to, label, icon: Icon }) => (
                <NavLink
                  key={to}
                  to={to}
                  end={to === "/"}
                  data-testid={`nav-${label.toLowerCase().replace(/ /g, "-")}`}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                      isActive ? "bg-blue-600 text-white" : "text-zinc-400 hover:text-white hover:bg-zinc-900"
                    }`
                  }
                >
                  <Icon className="h-4 w-4" aria-hidden="true" />
                  {label}
                </NavLink>
              ))}
            </div>
          ))}
          <div className="space-y-1">
            {COMING_SOON.map((c) => (
              <div key={c.label} className="px-3 py-1.5" data-testid={`nav-coming-${c.label.toLowerCase()}`}>
                <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-600">{c.label}</p>
                <p className="text-[11px] text-zinc-700">{c.note}</p>
              </div>
            ))}
          </div>
        </nav>
        <div className="px-4 py-4 border-t border-zinc-800 space-y-3">
          <div className="text-xs">
            <p className="text-white font-medium truncate" data-testid="user-email">{user?.email}</p>
            <p className="text-zinc-500 uppercase tracking-[0.2em] text-[10px] mt-0.5">{user?.role}</p>
          </div>
          <Button variant="ghost" size="sm" onClick={logout} data-testid="logout-button"
            className="w-full justify-start text-zinc-400 hover:text-white hover:bg-zinc-900 px-2">
            <LogOut className="h-4 w-4 mr-2" aria-hidden="true" /> Log out
          </Button>
        </div>
      </aside>
      <div className="flex-1 lg:ml-60 flex flex-col min-w-0">
        <header className="sticky top-0 z-30 h-16 flex items-center justify-between gap-3 px-4 md:px-8 border-b border-border backdrop-blur-xl bg-background/90">
          <div className="flex items-center gap-2.5 min-w-0">
            <Button variant="outline" size="icon" onClick={() => setNavOpen(true)}
                    data-testid="nav-toggle" aria-label="Open menu"
                    className="lg:hidden rounded-full h-9 w-9 shrink-0">
              <Menu className="h-4 w-4" aria-hidden="true" />
            </Button>
            <h1 className="font-heading text-base md:text-lg font-semibold tracking-tight truncate" data-testid="page-title">{current.title}</h1>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <span className="hidden xl:inline text-xs text-muted-foreground tracking-[0.2em] uppercase">Amazon · Flipkart · Website</span>
            <InstallPrompt />
            <Button variant="outline" size="icon" onClick={() => setDark(!dark)} data-testid="theme-toggle" className="rounded-full h-9 w-9">
              {dark ? <Sun className="h-4 w-4" aria-hidden="true" /> : <Moon className="h-4 w-4" aria-hidden="true" />}
            </Button>
          </div>
        </header>
        <main className="flex-1 p-4 md:p-8 min-w-0 max-w-full overflow-x-hidden">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
